import React from 'react';
import { useLoaderData, Await } from 'react-router-dom';

import {
  Action,
  CreateHtmlDocumentTemplateMutation,
  HtmlDocumentTemplateFieldsFragment,
  Resource
} from '~/api/graphql';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { PermissionButton } from '~/components/permissions';

import { CreateTemplateWorkspaceDialog } from '~/components/library/create-template-workspace-dialog';

import { HtmlEditorPage } from '~/components/library/html-editor-page';
import { HtmlTemplateListPage } from '~/components/library/html-template-list-page';
import { LibraryEditOverlayLoader } from '~/components/library/library-edit-overlay-loader';
import { StarterTemplatesDialog } from '~/components/library/starter-templates-dialog';
import { useActionValidationErrors } from '~/components/library/hooks/use-action-validation-errors';

const CREATE_TEMPLATE = [{ resource: Resource.HtmlDocumentTemplate, action: Action.Create }];

/**
 * Templates tab of the library. Lists every HTML template that has a body
 * (i.e. excludes pure styles). The only authoring entrypoint is the
 * AI-driven `CreateTemplateWorkspaceDialog`, which kicks off a Cara
 * workspace; the user saves the workspace's drafted document back to the
 * library from inside the workspace UI. There is intentionally no manual
 * blank-template create form.
 */
export const LibraryTemplatesPage = () => {
  const [showCreateDialog, setShowCreateDialog] = React.useState(false);
  const [showStarters, setShowStarters] = React.useState(false);
  const designsOn = useFlag(FEATURE_FLAGS.AGENCY_BRAND_DESIGN);
  return (
    <>
      <HtmlTemplateListPage
        kind="templates"
        extraActions={
          <>
            {designsOn && (
              <PermissionButton
                permissions={CREATE_TEMPLATE}
                whenDenied="hide"
                color="white"
                type="button"
                className="min-h-12"
                onClick={() => setShowStarters(true)}
              >
                Start from a starter
              </PermissionButton>
            )}
            <PermissionButton
              permissions={CREATE_TEMPLATE}
              whenDenied="hide"
              color="sky"
              type="button"
              className="min-h-12"
              onClick={() => setShowCreateDialog(true)}
            >
              Create Template
            </PermissionButton>
          </>
        }
      />
      <CreateTemplateWorkspaceDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
      />
      {designsOn && (
        <StarterTemplatesDialog open={showStarters} onClose={() => setShowStarters(false)} />
      )}
    </>
  );
};

/** Edit flow for an existing template; body is editable via the TipTap pane. */
export const TemplateEditPage = () => {
  const loader = useLoaderData() as
    | { data: Promise<{ htmlDocumentTemplate?: HtmlDocumentTemplateFieldsFragment }> }
    | undefined;
  const validationErrors = useActionValidationErrors<CreateHtmlDocumentTemplateMutation>();

  return (
    <React.Suspense fallback={<LibraryEditOverlayLoader />}>
      <Await resolve={loader?.data}>
        {(data: { htmlDocumentTemplate?: HtmlDocumentTemplateFieldsFragment } | undefined) => {
          const template = data?.htmlDocumentTemplate;
          if (!template) return null;
          return (
            <HtmlEditorPage
              kind="template"
              editMode
              initialTemplate={template}
              initialForm={{
                title: template.title,
                description: template.description ?? '',
                baseStyleId: template.baseStyleId ?? '',
                prefixFileId: template.prefixFiles?.[0]?.id ?? null,
                suffixFileId: template.suffixFiles?.[0]?.id ?? null,
                bodyTemplate: template.bodyTemplate ?? ''
              }}
              validationErrors={validationErrors}
              listUrl="/app/library/templates"
            />
          );
        }}
      </Await>
    </React.Suspense>
  );
};
